import deprecations from "./deprecation.js"

let degreesToRadians = Math.PI / 180


function Vector (params, b = false) { //eslint-disable-line complexity
    this._private = {}
    if (b !== false) {
        this.x = params
        this.y = b
    } else if ("x" in params) {
        this.x = params.x
        this.y = params.y
    } else if ("from" in params) {
        const {from, to} = params
        this.x = to.x - from.x
        this.y = to.y - from.y
        if ("length" in params) {
            this.setLength$(params.length)
        }
    } else if ("direction" in params || "angle" in params) {
        const {length = 1, angle = 0, direction = angle} = params
        this.direction = direction
        this.length    = length
    }
}


Object.defineProperty(Vector.prototype, "length", {
    enumerable: true,
    get: function () {
        if (this._private.radialOutdated) {
            updateRadial(this._private)
        }
        return this._private.length
    },
    set: function (value) {
        this.setLength$(value)
    }
})


function directionGetter () {
    if (this._private.radialOutdated) {
        updateRadial(this._private)
    }
    return this._private.direction
}

function directionRadGetter () {
    return this.direction * degreesToRadians
}

function directionSetter (degValue) {
    const {_private} = this
    if (_private.radialOutdated) {
        const {x, y} = _private
        _private.length = Math.sqrt(x * x + y * y)
        _private.radialOutdated = false
    }
    _private.direction = degValue
    _private.xyOutdated = true
    return this
}

function directionRadSetter (radValue) {
    return this.setDirection$(radValue / degreesToRadians)
}



function updateXY (privateData) {
    const radDirection = degreesToRadians * privateData.direction
    const {length = 1} = privateData
    privateData.x = length * Math.cos(radDirection)
    privateData.y = length * Math.sin(radDirection)
    privateData.xyOutdated = false
}



function updateRadial (privateData) {
    const {x, y} = privateData
    privateData.length = Math.sqrt(x * x + y * y)
    if (privateData.length > 0) {
        privateData.direction = Math.atan2(y, x) / degreesToRadians
    } else if (!privateData.direction) {
        privateData.direction = 0
    }
    privateData.radialOutdated = false
}


Object.defineProperty(Vector.prototype, "x", {
    enumerable: true,
    get: function () {
        if (this._private.xyOutdated) {
            updateXY(this._private)
        }
        return this._private.x
    },
    set: function (value) {
        const {_private} = this
        if (_private.xyOutdated) {
            const radDirection = degreesToRadians * _private.direction
            const {length = 1} = _private
            _private.y = length * Math.sin(radDirection)
            _private.xyOutdated = false
        }
        _private.radialOutdated = true
        _private.x = value
    }
})


Object.defineProperty(Vector.prototype, "y", {
    enumerable: true,
    get: function () {
        if (this._private.xyOutdated) {
            updateXY(this._private)
        }
        return this._private.y
    },
    set: function (value) {
        const {_private} = this
        if (_private.xyOutdated) {
            const radDirection = degreesToRadians * _private.direction
            const {length = 1} = _private
            _private.x = length * Math.cos(radDirection)
            _private.xyOutdated = false
        }
        _private.radialOutdated = true
        _private.y = value
    }
})


Object.defineProperty(Vector.prototype, "angle", {
    enumerable: false,
    get: directionGetter,
    set: directionSetter
})

Object.defineProperty(Vector.prototype, "angleRad", {
    enumerable: false,
    get: directionRadGetter,
    set: directionRadSetter
})


Object.defineProperty(Vector.prototype, "direction", {
    enumerable: true,
    get: directionGetter,
    set: directionSetter
})

Object.defineProperty(Vector.prototype, "directionRad", {
    enumerable: false,
    get: directionRadGetter,
    set: directionRadSetter
})




Vector.prototype.isNull = function () {
    if (this._private.radialOutdated) {
        return this.x === 0 && this.y === 0
    } else {
        return this.length === 0
    }
}


Vector.prototype.clone = function () {
    return new Vector(this)
}



Vector.prototype.plus = function ({x, y}) {
    return new Vector(this.x + x, this.y + y)
}

Vector.prototype.add = function ({x, y}) {
    this.x += x
    this.y += y
    return this
}

Vector.prototype.plus$ = Vector.prototype.add




Vector.prototype.minus = function ({x, y}) {
    return new Vector(this.x - x, this.y - y)
}

Vector.prototype.subtract = function ({x, y}) {
    this.x -= x
    this.y -= y
    return this
}

Vector.prototype.minus$ = Vector.prototype.subtract




Vector.prototype.multiply = function (number) {
    if (this._private.radialOutdated) {
        return new Vector(this.x * number, this.y * number)
    } else {
        return new Vector({length: this.length * number, direction: this.direction})
    }
}

Vector.prototype.multiply$ = function (number) {
    if (this._private.radialOutdated) {
        this.x *= number
        this.y *= number
    } else {
        this.length *= number
    }
    return this
}




Vector.prototype.setLength = function (newLength) {
    return new Vector({length: newLength, direction: this.direction})
}

Vector.prototype.setLength$ = function (newLength) {
    if (this._private.radialOutdated) {
        let {x, y} = this._private
        if (x !== 0 || y !== 0) {
            this._private.direction = Math.atan2(y, x) / degreesToRadians
        }
        this._private.radialOutdated = false
    }
    this._private.xyOutdated = true
    if (newLength < 0) {
        this._private.length = -newLength
        this._private.direction += 180
    } else {
        this._private.length = newLength
    }

    return this
}




Vector.prototype.normalize = function () {
    return this.setLength(1)
}

Vector.prototype.normalize$ = function () {
    return this.setLength$(1)
}




Vector.prototype.addToLength = function (deltaLength) {
    return this.setLength(this.length + deltaLength)
}

Vector.prototype.addToLength$ = function (deltaLength) {
    return this.setLength$(this.length + deltaLength)
}



Vector.prototype.rotate90 = function () {
    return new Vector(-this.y, this.x)
}

Vector.prototype.rotate90$ = function () {
    const oldX = this.x
    this.x = -this.y
    this.y = oldX
    return this
}


Vector.prototype.rotate = function (degValue) {
    return this.rotateRad(degValue * degreesToRadians)
}

Vector.prototype.rotate$ = function (degValue) {
    this.direction += degValue
    return this
}


Vector.prototype.rotateRad = function (radValue) {
    const sine   = Math.sin(radValue)
    const cosine = Math.cos(radValue)
    return new Vector(
        this.x * cosine - this.y * sine,
        this.x * sine   + this.y * cosine
    )
}

Vector.prototype.rotateRad$ = function (radValue) {
    this.direction += radValue / degreesToRadians
    return this
}



Vector.prototype.setDirection = function (degValue) {
    return new Vector({length: this.length, direction: degValue})
}

Vector.prototype.setDirection$ = directionSetter

Vector.prototype.setDirectionRad$ = directionRadSetter




Vector.prototype.dotProduct = function ({x, y}) {
    return this.x * x + this.y * y
}

Vector.prototype.crossProduct = function ({x, y}) {
    return this.x * y - this.y * x
}

Vector.prototype.projectOn = function (unitVector) {
    return unitVector.multiply(this.dotProduct(unitVector))
}





deprecations.add("Vector", Vector.prototype, {
    substract: "subtract"
}, {onlyWhenCalled: true})


export default Vector


